import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router';

import { ShopScreen } from '../shop-base/ShopScreen';

import { getContentfulBrandThunk } from 'common/features/store/duck/brands/duck';
import { Product } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'rootStore';
import { getProductsByBrand } from 'common/api/e-comm/brands';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { UnAuthProductDetailsView } from './views/UnAuthProductDetailsView';
import { Routes } from 'routes';
import { Metadata } from 'components/metadata';
import { MetadataPage } from 'components/metadata/constants';
import { DetailsProps, ProductDetailsScreenParams } from './components/utils/types';
import { selectIsConstructorBrowseEnabledWeb } from 'common/features/store/duck/ffs';
import { ProductDetailScreenContentConstructor } from './views/ProductDetailScreenContentConstructor';
import { ProductDetailScreenContentCognitive } from './views/ProductDetailScreenContentCognitive';
import { constructorClient } from 'third-party-integrations/constructor';
import { useConstructorBrandById } from 'common/api/constructor/useConstructorBrandById';
import { isAffiliateOnlyBrand } from 'common/utils/affiliate';

export const ProductDetailsScreenContent = (props: DetailsProps) => {
    const isConstructorAffiliateEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);

    return isConstructorAffiliateEnabled ? (
        <ProductDetailScreenContentConstructor {...props} />
    ) : (
        <ProductDetailScreenContentCognitive {...props} />
    );
};

const useBrandId = () => {
    const brandId = useParams<ProductDetailsScreenParams>().id;
    let id: number | string;
    if (isAffiliateOnlyBrand(brandId)) {
        const [, numericId] = brandId.split('-');
        id = `affiliate-${numericId}`;
    } else {
        id = parseInt(brandId, 10);
    }

    return id;
};

export const ProductDetailsScreen = () => {
    useScrollToTop();
    const history = useHistory();
    const brandId = useBrandId();
    const params = useParams<{ id: string }>();
    const brandName = history.location.state?.brandName || '';
    const backRoute = history.location.state?.backRoute || '';
    const [products, setProducts] = useState<Product[]>();
    const brands = useRootSelector((state) => state.store.brands);
    const isAuthenticated = useRootSelector((state) => state.store.account.userId);
    const isConstructorAffiliateEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);

    const {
        brand,
        constructorBrand,
        isAffiliateOnlyBrand,
        isGiftCardOnlyBrand,
        constructorBrandHasLoaded,
    } = useConstructorBrandById(isConstructorAffiliateEnabled, constructorClient, brandId);

    const dispatch = useDispatch<any>();

    useLayoutEffect(() => {
        const [brandDetailsPath, inBulkPath] = history.location.pathname.split('/gifting');
        if (inBulkPath === '') {
            history.replace(brandDetailsPath);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (typeof brandId !== 'number') {
            setProducts([]);
            return;
        }

        const loadProducts = async () => {
            const result = await getProductsByBrand(brandId);

            if (result.error) {
                return;
            }

            setProducts(result.data);
        };
        loadProducts();

        // retrieve terms from Contentful
        getContentfulBrandThunk(brandId)(dispatch);
    }, [dispatch, brandId]);

    useEffect(() => {
        if (constructorBrandHasLoaded && !brand && !constructorBrand) {
            history.replace(Routes.Brands);
        }
    }, [
        history,
        brand,
        constructorBrand,
        constructorBrandHasLoaded,
        isConstructorAffiliateEnabled,
    ]);

    return (
        <Metadata
            page={MetadataPage.pdp}
            brandName={brandName || brand?.name || constructorBrand?.name}
        >
            <ShopScreen>
                {isAuthenticated ? (
                    <ProductDetailsScreenContent
                        brandId={typeof brandId === 'number' ? brandId : 0}
                        brand={brand}
                        constructorBrand={constructorBrand}
                        brands={brands}
                        products={products}
                        isLoadingBrandInfo={!constructorBrandHasLoaded}
                        isAffiliateOnlyBrand={isAffiliateOnlyBrand}
                        isGiftCardOnlyBrand={isGiftCardOnlyBrand}
                        backRoute={backRoute}
                        idWithSlug={params.id}
                    />
                ) : (
                    <UnAuthProductDetailsView brand={brand} products={products} />
                )}
            </ShopScreen>
        </Metadata>
    );
};

export default ProductDetailsScreen;
